/**
 * Web Vitals Observer
 * Captures Core Web Vitals (LCP, FID/INP, CLS, FCP, TTFB) using standard PerformanceObserver.
 */

import { telemetryService } from './telemetryService';
import type { TelemetryRating } from './telemetryTypes';

function getRating(name: string, value: number): TelemetryRating {
  switch (name) {
    case 'LCP':
      return value <= 2500 ? 'good' : value <= 4000 ? 'needs-improvement' : 'poor';
    case 'FID':
      return value <= 100 ? 'good' : value <= 300 ? 'needs-improvement' : 'poor';
    case 'INP':
      return value <= 200 ? 'good' : value <= 500 ? 'needs-improvement' : 'poor';
    case 'CLS':
      return value <= 0.1 ? 'good' : value <= 0.25 ? 'needs-improvement' : 'poor';
    case 'FCP':
      return value <= 1800 ? 'good' : value <= 3000 ? 'needs-improvement' : 'poor';
    case 'TTFB':
      return value <= 800 ? 'good' : value <= 1800 ? 'needs-improvement' : 'poor';
    default:
      return 'good';
  }
}

export function initWebVitals(): void {
  if (typeof window === 'undefined' || typeof PerformanceObserver === 'undefined') {
    return;
  }

  // 1. TTFB & FCP via paint / navigation
  try {
    const navEntries = performance.getEntriesByType('navigation') as PerformanceNavigationTiming[];
    if (navEntries && navEntries.length > 0) {
      const nav = navEntries[0];
      const ttfb = nav.responseStart - nav.requestStart;
      if (ttfb > 0) {
        telemetryService.recordWebVital({
          name: 'TTFB',
          value: ttfb,
          rating: getRating('TTFB', ttfb),
          id: `ttfb-${Date.now()}`,
        });
      }
    }
  } catch {
    // Navigation timing not supported or empty
  }

  // 2. FCP (First Contentful Paint)
  try {
    const paintObserver = new PerformanceObserver((entryList) => {
      for (const entry of entryList.getEntries()) {
        if (entry.name === 'first-contentful-paint') {
          const value = entry?.startTime ?? 0;
          telemetryService.recordWebVital({
            name: 'FCP',
            value,
            rating: getRating('FCP', value),
            id: `fcp-${Date.now()}`,
          });
          paintObserver.disconnect();
        }
      }
    });
    paintObserver.observe({ type: 'paint', buffered: true });
  } catch {
    // Paint timing not supported
  }

  // 3. LCP (Largest Contentful Paint)
  try {
    let largestEntry: PerformanceEntry | null = null;
    const lcpObserver = new PerformanceObserver((entryList) => {
      const entries = entryList.getEntries();
      if (entries && entries.length > 0) {
        largestEntry = entries[entries.length - 1];
      }
    });
    lcpObserver.observe({ type: 'largest-contentful-paint', buffered: true });

    const reportLcp = () => {
      if (largestEntry && typeof largestEntry.startTime === 'number') {
        const value = largestEntry.startTime;
        telemetryService.recordWebVital({
          name: 'LCP',
          value,
          rating: getRating('LCP', value),
          id: `lcp-${Date.now()}`,
        });
        lcpObserver.disconnect();
      }
    };

    window.addEventListener('visibilitychange', () => {
      if (document.visibilityState === 'hidden') reportLcp();
    }, { once: true });
    window.addEventListener('pagehide', reportLcp, { once: true });
  } catch {
    // LCP not supported
  }

  // 4. CLS (Cumulative Layout Shift)
  try {
    let clsValue = 0;
    const clsObserver = new PerformanceObserver((entryList) => {
      for (const entry of entryList.getEntries() as (PerformanceEntry & { hadRecentInput?: boolean; value?: number })[]) {
        if (!entry.hadRecentInput && typeof entry.value === 'number') {
          clsValue += entry.value;
        }
      }
    });
    clsObserver.observe({ type: 'layout-shift', buffered: true });

    const reportCls = () => {
      telemetryService.recordWebVital({
        name: 'CLS',
        value: clsValue,
        rating: getRating('CLS', clsValue),
        id: `cls-${Date.now()}`,
      });
      clsObserver.disconnect();
    };

    window.addEventListener('visibilitychange', () => {
      if (document.visibilityState === 'hidden') reportCls();
    }, { once: true });
  } catch {
    // CLS not supported
  }

  // 5. INP / FID (Interaction to Next Paint / First Input Delay)
  try {
    const inpObserver = new PerformanceObserver((entryList) => {
      for (const entry of entryList.getEntries() as (PerformanceEntry & { processingStart?: number; duration?: number })[]) {
        const duration = entry?.duration ?? (entry?.processingStart && entry?.startTime ? entry.processingStart - entry.startTime : 0);
        telemetryService.recordWebVital({
          name: 'INP',
          value: duration,
          rating: getRating('INP', duration),
          id: `inp-${Date.now()}`,
        });
      }
    });
    inpObserver.observe({ type: 'first-input', buffered: true });
  } catch {
    // Input timing not supported
  }
}
